/** The tabs view (plan D1, D2 — phase 5): the booking one facet at a time — Booking ·
 *  Guest · Money · Check-in · Integration · Activity · Communication (`FacetTabs`) —
 *  inside the Booking page tab. It lays out the same section registry as the spine
 *  (`tabsSections`), with the same stage panels and FieldSlots, on the page's one draft,
 *  ledger and Save: switching the view or the tab never touches the draft, and an edit
 *  made on one tab shows on every other surface that renders the field.
 *
 *  The open tab's sections stack in one full-width column — the width the panels have in
 *  edit mode, in both modes, with the field grids pinned to it, so entering edit mode
 *  never re-lays anything out (the spine's rule, `spineColumns`). The Integration and
 *  Activity tabs hold the booking's provenance and reviews; each names the page tab
 *  that holds the rest of that story. */
import { Box, Link, Typography } from '@mui/material';
import { forwardRef, useMemo, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingRecordFacet } from '@/api/bookingRecord';
import { FIELD_GRID_VAR, fieldGridColumns } from '@/features/records/panels/fieldGrid';
import { spineColumns } from '@/features/records/views/spineColumns';

import { bookingDisplayContext } from '../panels/displays';
import { STAGE_PANELS } from '../panels/stagePanels';
import { BOOKING_RECORD_REGISTRY } from '../registry';

import { managedByName, type BookingRecordData } from './spineLayout';
import { facetPanelId, facetTabId, tabsSections } from './tabsLayout';

/** The page tabs a facet points on to. */
export type FacetPageTab = 'activity' | 'integration';

export interface TabsViewProps {
  data: BookingRecordData;
  /** The page content's width (ledger column included); 0 = unmeasured. */
  width: number;
  facet: BookingRecordFacet;
  /** The page tab's href (keeps edit mode and the view). */
  pageTabHref: (tab: FacetPageTab) => string;
}

export const TabsView = forwardRef<HTMLDivElement, TabsViewProps>(function TabsView(
  { data, width, facet, pageTabHref },
  ref,
) {
  const { editWidth } = spineColumns(width);
  const { catalog, editContext, detail } = data;
  const sections = useMemo(
    () => tabsSections(BOOKING_RECORD_REGISTRY, { catalog, editContext, detail }),
    [catalog, editContext, detail],
  );
  const display = useMemo(() => bookingDisplayContext(detail, editContext), [detail, editContext]);
  const shown = sections.filter((s) => s.facet === facet);
  const restech = managedByName(editContext);

  return (
    <Box
      ref={ref}
      role="tabpanel"
      id={facetPanelId(facet)}
      aria-labelledby={facetTabId(facet)}
      tabIndex={-1}
      data-tabs-view
      data-facet={facet}
      sx={{
        display: 'grid',
        gap: 2,
        alignContent: 'start',
        minWidth: 0,
        // Clears the sticky tab bar when a tab switch brings the panel back into view.
        scrollMarginTop: 'var(--record-spine-height, 0px)',
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        ...(width > 0 ? { [FIELD_GRID_VAR]: fieldGridColumns(editWidth) } : {}),
      }}
    >
      {shown.map((s) => {
        const Panel = STAGE_PANELS[s.id];
        // Keyed by tab too: a stage split over tabs is a fresh panel on each.
        return <Panel key={`${s.facet}:${s.id}`} section={s} display={display} editWidth={width > 0 ? editWidth : 0} />;
      })}
      {shown.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Nothing on this tab for this booking.
        </Typography>
      )}
      {facet === 'integration' && (
        <PageTabPointer href={pageTabHref('integration')} tab="Integration">
          What the feed sent, the raw {restech ?? 'booking system'} data, the import runs and the API requests are on the
        </PageTabPointer>
      )}
      {facet === 'activity' && (
        <PageTabPointer href={pageTabHref('activity')} tab="Activity">
          The change history, the audit entries and the deliveries are on the
        </PageTabPointer>
      )}
    </Box>
  );
});

function PageTabPointer({ href, tab, children }: { href: string; tab: string; children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}{' '}
      <Link component={RouterLink} to={href} underline="hover">
        {tab} tab
      </Link>
      .
    </Typography>
  );
}
